import { render as preactRender } from "preact"
import { useEffect, useState } from "preact/hooks"

import type {
  ConfigSnapshotPayload,
  EmbedKind,
  GroupPayload,
  WidgetPayload,
} from "@workspace/contracts/embed"

import type { Api } from "./api"
import { BASE_CSS } from "./base.css"
import { createReporter, type Reporter } from "./events"
import { armAdvance, playingMembers, sequenceMember } from "./group"
import { hostIsDark, pageContext, type Theme } from "./host"
import { runRules } from "./rules"
import { Surface, type Kick } from "./surface"

/**
 * `render(snapshot, host, options)` - the runtime's one entry: a shadow
 * root on `host`, the base stylesheet plus the customer's CSS adopted into
 * it, the payload rendered inside. The custom elements (`element.ts`) call
 * it with what the API served; the dashboard's playground will call it
 * with the working copy (plan E3), which is why it takes a payload and a
 * host rather than an id.
 */

export interface MountOptions {
  /** The API origin. Defaults to the one baked at build time. */
  apiUrl?: string
  /** Force the host's darkness rather than sampling the page. */
  theme?: Theme | null
  /** Skip the events batch - a preview mount that must not count. */
  silent?: boolean
}

export interface Mount {
  /** Re-render with another payload (the playground's working copy). */
  update: (snapshot: ConfigSnapshotPayload) => void
  unmount: () => void
}

/** The customer's CSS, scoped: `customCss` as is, each block's `css` under its hook. */
function customCssOf(widget: WidgetPayload): string {
  const parts: string[] = []
  const custom = widget.config.appearance.customCss.trim()
  if (custom) parts.push(custom)
  if (widget.config.type === "exit-popup") {
    widget.config.blocks.forEach((block, index) => {
      const css = block.css.trim()
      if (css) {
        parts.push(
          `[data-uq-widget="${widget.id}"] [data-uq-block="${index}"]{${css}}`
        )
      }
    })
  }
  return parts.join("\n")
}

function widgetsOf(snapshot: ConfigSnapshotPayload): WidgetPayload[] {
  return snapshot.kind === "widget"
    ? [snapshot.payload]
    : snapshot.payload.members.map((m) => m.widget)
}

/** A silent reporter: the playground's mount counts nothing. */
const quiet: Reporter = { report: () => {}, flush: () => {} }

function GroupApp({
  group,
  ...rest
}: {
  group: GroupPayload
  api: Api
  embed: { kind: EmbedKind; id: string }
  page: ReturnType<typeof pageContext>
  reporter: Reporter
  hostDark: boolean
}) {
  // The group's rules say when it plays; with none it plays at once.
  const [playing, setPlaying] = useState(group.rules.length === 0)
  const [kick, setKick] = useState<Kick | null>(null)
  const [members, setMembers] = useState(() => playingMembers(group))

  useEffect(
    () =>
      runRules(group.rules, rest.page, (rule) => {
        rest.reporter.report("rule.fired", {
          payload: { ruleId: rule.id, trigger: rule.trigger },
        })
        setPlaying(true)
        setKick((prev) => ({
          action: rule.actionConfig,
          n: (prev?.n ?? 0) + 1,
        }))
      }),
    [group.id]
  )

  // A sequence moves on when the current member's advance trigger fires.
  useEffect(() => {
    if (group.mode !== "sequence" || !playing) return
    const current = sequenceMember(group)
    if (!current) return
    return armAdvance(group, current, (next) =>
      setMembers(next ? [next.widget] : [])
    )
  }, [group.id, group.mode, playing, members])

  if (!playing) return null
  return (
    <>
      {members.map((widget) => (
        <div
          key={widget.id}
          data-uq-widget={widget.id}
          style="display:contents"
        >
          <Surface payload={widget} kick={kick} {...rest} />
        </div>
      ))}
    </>
  )
}

function App({
  snapshot,
  ...rest
}: {
  snapshot: ConfigSnapshotPayload
  api: Api
  page: ReturnType<typeof pageContext>
  reporter: Reporter
  hostDark: boolean
}) {
  const embed = { kind: snapshot.kind, id: snapshot.payload.id }
  if (snapshot.kind === "group") {
    return <GroupApp group={snapshot.payload} embed={embed} {...rest} />
  }
  return (
    <div data-uq-widget={snapshot.payload.id} style="display:contents">
      <Surface payload={snapshot.payload} embed={embed} {...rest} />
    </div>
  )
}

export function render(
  snapshot: ConfigSnapshotPayload,
  host: HTMLElement,
  options: MountOptions = {}
): Mount {
  const root = host.shadowRoot ?? host.attachShadow({ mode: "open" })
  const api: Api = {
    base: (options.apiUrl ?? __USEREQ_API_URL__).replace(/\/+$/, ""),
  }
  const embed = { kind: snapshot.kind, id: snapshot.payload.id }
  const reporter = options.silent ? quiet : createReporter(api, embed)
  const hostDark = hostIsDark(options.theme ?? null)
  const page = pageContext()

  // The sheet: the base, then the customer's. A <style> in the root rather
  // than a constructed sheet, so Safari before 16.4 is not left unstyled.
  const style = document.createElement("style")
  root.appendChild(style)
  const target = document.createElement("div")
  target.setAttribute("data-uq-root", "")
  target.style.display = "contents"
  root.appendChild(target)

  const paint = (next: ConfigSnapshotPayload) => {
    style.textContent = [BASE_CSS, ...widgetsOf(next).map(customCssOf)].join(
      "\n"
    )
    preactRender(
      <App
        snapshot={next}
        api={api}
        page={page}
        reporter={reporter}
        hostDark={hostDark}
      />,
      target
    )
  }
  paint(snapshot)
  reporter.report("embed.loaded", {
    payload: { version: snapshot.payload.version },
  })

  return {
    update: paint,
    unmount: () => {
      preactRender(null, target)
      reporter.flush()
      style.remove()
      target.remove()
    },
  }
}
